import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { computed, enableProdMode, importProvidersFrom, inject, provideZonelessChangeDetection } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideRouter, TitleStrategy, withNavigationErrorHandler } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { AuthModule } from '@auth0/auth0-angular';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { SuiTheme, SuiThemeService } from '@semeris/ui';
// `/chat-config`, never `/chat` — the tokens must be root-provided (ChatStateManager and
// AgentApi are `providedIn: 'root'` and inject them), and naming one through the `/chat`
// barrel would pull the whole chat UI into the initial bundle instead of the lazy routes.
import { provideWorkspaceUi } from '@semeris/workspace-ui';
import { CHAT_CONFIG, CHAT_THEME } from '@semeris/workspace-ui/chat-config';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/routes';
import { environment } from './environments/environment';
import { sdocsChatConfig } from './features/ai-chat/chat-config';
import { aiWorkspaceAuthInterceptor } from './features/ai-chat/interceptors/ai-workspace-auth.interceptor';
import { workspaceImpersonateInterceptor } from './features/ai-chat/interceptors/workspace-impersonate.interceptor';
import { AssetInterceptor } from './features/asset/service/asset.interceptor';
import { LoggingModule } from './features/logging/logging.module';
import { ConsoleLoggerService } from './features/logging/services/providers/console-logger.service';
import { RaygunLoggerService } from './features/logging/services/providers/raygun-logger.service';
import { ApiRequestInterceptor as SessionSecurityInterceptor } from './features/shared/interceptors/api-request.interceptor';
import { AuthInterceptor } from './features/shared/interceptors/auth.interceptor';
import { ResolutionInterceptor } from './features/shared/interceptors/resolution.interceptor';
import { WebSocketInterceptor } from './features/shared/interceptors/web-socket.interceptor';
import { BreadcrumbTitleStrategy } from './features/shared/services/breadcrumb-title.strategy';
import { provideSuiDateFormat } from './features/shared/services/date-format/provide-sui-date-format';
import { handleNavigationError } from './features/shared/utils/navigation-error-handler';
import { provideWorkspaceLink } from './features/shared/utils/provide-workspace-link';
import { registerRuntimeStats } from './features/shared/utils/runtime-stats';
import { workspaceActivityInterceptor } from './features/workspace-ui/interceptors/workspace-activity.interceptor';
import { workspaceUiApiBase } from './features/workspace-ui/workspace-api-base';
import {
  sdocsWorkspaceChrome,
  sdocsWorkspaceContext,
  sdocsWorkspaceNav,
  sdocsWorkspacePermissions,
} from './features/workspace-ui/workspace-ui.config';
import { devTools } from './store/dev-tools';
import { RootStoreModule } from './store/root-store.module';

const importedDevTools = environment.production ? [] : [devTools];

registerRuntimeStats();

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideWorkspaceLink(),
    provideSuiDateFormat(),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      LoggingModule.forRoot({
        enableConsoleLog: !environment.production,
        logProviders: environment.production ? [RaygunLoggerService] : [ConsoleLoggerService],
        sensitiveActions: [],
      }),
      AuthModule.forRoot({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: {
          // eslint-disable-next-line camelcase
          redirect_uri:
            typeof environment.auth0.loginRedirectUri === 'function'
              ? environment.auth0.loginRedirectUri()
              : environment.auth0.loginRedirectUri,
          scope: environment.auth0.scope,
          audience: environment.auth0.audience,
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        // v2 of the SDK throws MissingRefreshTokenError when the cached refresh token is missing
        // or rejected. Fall back to the silent iframe flow (the pre-refresh-token behaviour) instead.
        useRefreshTokensFallback: true,
        skipRedirectCallback: window.location.pathname.includes('/auth/login/callback'),
      }),
      ...importedDevTools,
      RootStoreModule,
      StoreRouterConnectingModule.forRoot({
        navigationActionTiming: NavigationActionTiming.PostActivation,
      }),
      ServiceWorkerModule.register('safety-worker.js', {
        enabled: true,
        registrationStrategy: 'registerImmediately',
      }),
    ),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withNavigationErrorHandler(handleNavigationError)),
    { provide: TitleStrategy, useClass: BreadcrumbTitleStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: SessionSecurityInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AssetInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WebSocketInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResolutionInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([workspaceImpersonateInterceptor, aiWorkspaceAuthInterceptor, workspaceActivityInterceptor])),
    // The whole workspace-ui host contract — all five tokens, because sdocs mounts the
    // package's own assistant screen (`ASSISTANT_ROUTES`), not just `<zing-chat>`.
    provideWorkspaceUi({
      apiBase: workspaceUiApiBase,
      context: sdocsWorkspaceContext,
      nav: sdocsWorkspaceNav,
      permissions: sdocsWorkspacePermissions,
      chrome: sdocsWorkspaceChrome,
    }),
    { provide: CHAT_CONFIG, useFactory: sdocsChatConfig },
    // Maps SuiThemeService → CHAT_THEME so chat charts/diagrams follow dark mode. At root, so
    // it also covers an artifact preview rendering <zing-echarts-chart> outside the chat host.
    {
      provide: CHAT_THEME,
      useFactory: () => {
        const theme = inject(SuiThemeService);
        return computed(() => (theme.activeTheme() === SuiTheme.Dark ? 'dark' : 'light'));
      },
    },
  ],
}).catch(console.error);
